import React, { useMemo } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import {
	type DefaultValues,
	type FieldValues,
	type SubmitErrorHandler,
	type SubmitHandler,
	useForm,
	type UseFormProps
} from 'react-hook-form';
import * as z from 'zod/v4-mini';

import { Form } from '@/components/ui/form';

type HookFormProviderProps<
	TValues extends FieldValues,
	TSchema extends z.input<TValues>
> = {
	children: React.ReactNode,
	schema: TSchema,
	defaultValues?: NullableDefaultValues<z.output<TSchema>>,
	onSubmit: SubmitHandler<TValues>,
	onInvalid?: SubmitErrorHandler<TValues>,
	formOptions?: StrictOmit<UseFormProps<TValues>, 'defaultValues' | 'resolver'>
} & StrictOmit<React.FormHTMLAttributes<HTMLFormElement>, 'onSubmit'>;

type NullableDefaultValues<T> = {
	[K in keyof T]?: T[K] | null
};

const HookFormProvider = <
	TValues extends FieldValues,
	TSchema extends z.input<TValues>
>({
	children,
	schema,
	defaultValues,
	onSubmit,
	onInvalid,
	formOptions,
	...props
}: HookFormProviderProps<TValues, TSchema>) => {
	const memoizedDefaultValues = useMemo(
		() => defaultValues,
		[defaultValues]
	) as DefaultValues<TValues>;

	const methods = useForm({
		mode: 'onSubmit',
		resolver: zodResolver<TValues, unknown, TValues>(schema),
		reValidateMode: 'onBlur',
		defaultValues: memoizedDefaultValues,
		...formOptions
	});

	return (
		<Form {...methods}>
			<form
				{...props}
				onSubmit={(e) => {
					e.stopPropagation();
					methods.handleSubmit(onSubmit, onInvalid)(e);
				}}
			>
				{children}
			</form>
		</Form>
	);
};

export default HookFormProvider;
